'use client';

import { Suspense, useState } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import { Button, Typography } from '@/shared-ui';
import { LoadMore, type LoadMoreProps } from '../../molecules/load-more';
import { ProductGrid, type ProductGridItem, type ProductGridProps } from '../product-grid';

export type ProductResultsProps = Omit<
    ProductGridProps,
    'data' | 'isLoading' | 'skeletonCount' | 'firstNewItemIndex' | 'newItemsStartIndex'
> & {
    products: ProductGridItem[];
    skeletonCount?: number;
    isGridLoading?: boolean;
    isRestoring?: boolean;
    isLoadingMore?: boolean;
    loadMore?: LoadMoreProps;
    deferredProducts?: Promise<ProductGridItem[]>;
    deferredSkeletonCount?: number;
    appendedProducts?: ProductGridItem[];
    firstNewIndex?: number | null;
};

export const ProductResults = ({
    products,
    skeletonCount = 0,
    isGridLoading = false,
    isRestoring = false,
    isLoadingMore = false,
    loadMore,
    deferredProducts,
    deferredSkeletonCount = 0,
    appendedProducts = [],
    firstNewIndex = null,
    ...gridProps
}: ProductResultsProps) => {
    const { t } = useTranslation('category');
    const showSkeletons = isGridLoading || isRestoring;
    const shouldRenderDeferred = useDeferredRender(!showSkeletons && deferredSkeletonCount > 0);
    // ProductListing keys this component by listing identity, so navigation resets the pinned promise while
    // unrelated loader revalidation keeps the resolved grid mounted.
    const [pinnedDeferredProducts] = useState(() => deferredProducts);

    const productKey = (product: ProductGridItem) => String('productId' in product ? product.productId : product.id);

    const uniqueProducts = (items: ProductGridItem[]) => {
        const seen = new Set<string>();
        return items.filter((item) => {
            const key = productKey(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const renderGrid = (
        critical: ProductGridItem[],
        extra: ProductGridItem[] = [],
        skeletons = 0,
        appended: ProductGridItem[] = []
    ) => {
        const appendedStartIndex = critical.length + extra.length;
        return (
            <ProductGrid
                {...gridProps}
                critical={uniqueProducts(critical)}
                nonCritical={uniqueProducts(extra)}
                appended={uniqueProducts(appended)}
                skeletonCount={skeletons}
                isLoading={showSkeletons || isLoadingMore}
                firstNewItemIndex={firstNewIndex == null ? null : appendedStartIndex + firstNewIndex}
                newItemsStartIndex={appended.length > 0 ? appendedStartIndex : null}
            />
        );
    };

    const loadMoreControl =
        loadMore && !isGridLoading && !isRestoring ? <LoadMore {...loadMore} className="mt-4" /> : null;

    const deferredError = (
        <>
            {renderGrid(products, [], 0, appendedProducts)}
            <div role="alert" className="mt-4 flex flex-col items-center gap-4 text-center">
                <Typography variant="p" className="font-light">
                    {t('loadMore.error', { defaultValue: 'Something went wrong. Please try again.' })}
                </Typography>
                <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
                    {t('loadMore.retry', { defaultValue: 'Try again' })}
                </Button>
            </div>
        </>
    );

    const content = showSkeletons ? (
        renderGrid([], [], skeletonCount)
    ) : pinnedDeferredProducts && deferredSkeletonCount > 0 ? (
        shouldRenderDeferred ? (
            <Suspense fallback={renderGrid(products, [], 0, appendedProducts)}>
                <Await resolve={pinnedDeferredProducts} errorElement={deferredError}>
                    {(deferred) => (
                        <>
                            {renderGrid(products, deferred, 0, appendedProducts)}
                            {loadMoreControl}
                        </>
                    )}
                </Await>
            </Suspense>
        ) : (
            renderGrid(products, [], 0, appendedProducts)
        )
    ) : (
        <>
            {renderGrid(products, [], 0, appendedProducts)}
            {loadMoreControl}
        </>
    );

    return content;
};
